/**
 * Onboarding's Prefill card (`prefill_from_connected_apps` output): profile
 * values read from the member's own email signature and calendar, each with
 * where it came from. Nothing is saved yet: Fix edits a value in place, and
 * Looks Right sends the values (as edited) to the agent, which saves them.
 */
import type { DynamicToolUIPart, ToolUIPart } from "ai";
import { Check, CheckCircle2, Pencil, ScanSearch } from "lucide-react";
import { useMemo, useState, type FC } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useOfferActionsContext } from "@/features/caseIde/chat/offers/OfferActionsContext";
import { cn } from "@/lib/utils";

import type { PrefillOutput, PrefillProposal } from "../types";

type AnyToolPart = ToolUIPart | DynamicToolUIPart;

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);

export const prefillFromOutput = (output: unknown): PrefillOutput | null => {
  if (!isRecord(output) || !Array.isArray(output.proposals)) return null;
  const proposals = output.proposals.filter(
    (p): p is PrefillProposal =>
      isRecord(p) && typeof p.field === "string" && p.value != null
  );
  const strings = (v: unknown) =>
    Array.isArray(v) ? v.filter((s): s is string => typeof s === "string") : [];
  return {
    proposals,
    sources: strings(output.sources),
    notes: strings(output.notes)
  };
};

export const displayValue = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  if (Array.isArray(value)) return value.map(displayValue).join(", ");
  if (isRecord(value))
    return Object.values(value)
      .filter((v) => v != null && v !== "")
      .map(displayValue)
      .join(", ");
  return "";
};

const SOURCE_TEXT: Record<string, string> = {
  signature: "From your email signature",
  calendar: "From your calendar",
  pasted: "From what you pasted"
};

/** The member's confirmation, as the message the agent saves from. */
export const confirmationMessage = (
  proposals: readonly PrefillProposal[],
  edits: Readonly<Record<string, string>>
): string => {
  const lines = proposals.map((p) => {
    const edited = edits[p.field];
    const value =
      edited !== undefined && edited !== displayValue(p.value)
        ? `${edited} (corrected)`
        : displayValue(p.value);
    return `- ${p.label || p.field}: ${value}`;
  });
  return `These look right. Save them:\n${lines.join("\n")}`;
};

export const PrefillCard: FC<{ part: AnyToolPart; className?: string }> = ({
  part,
  className
}) => {
  const data = useMemo(
    () =>
      part.state === "output-available" ? prefillFromOutput(part.output) : null,
    [part]
  );
  const offers = useOfferActionsContext();
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [fixing, setFixing] = useState<string | null>(null);
  const [sent, setSent] = useState<"saved" | "skipped" | null>(null);
  if (!data || !data.proposals.length) return null;
  const { proposals } = data;

  if (sent) {
    return (
      <div
        className={cn(
          "flex items-center gap-2 rounded-md bg-fill-1 px-3 py-2 text-base",
          className
        )}
        data-testid="prefill-card-resolved"
      >
        <CheckCircle2
          className={cn(
            "size-3.5 shrink-0",
            sent === "saved" ? "text-success" : "text-fg-3"
          )}
        />
        <span className="min-w-0 flex-1 truncate font-medium">
          {sent === "saved"
            ? `Confirmed ${proposals.length} detail${
                proposals.length === 1 ? "" : "s"
              }`
            : "Skipped what I found"}
        </span>
      </div>
    );
  }

  const say = offers?.say;
  const busy = offers?.busy ?? true;

  return (
    <div
      className={cn(
        "rounded-xl bg-elevated p-3 text-base inset-ring inset-ring-line-2",
        className
      )}
      data-testid="prefill-card"
    >
      <div className="flex items-center gap-1.5 text-fg-3">
        <ScanSearch className="size-3.5 shrink-0" />
        <span className="flex-1">Here's What I Found</span>
        <span className="text-sm tabular-nums">
          {proposals.length} Detail{proposals.length === 1 ? "" : "s"}
        </span>
      </div>
      <p className="mt-1 text-sm text-fg-3">
        Nothing is saved yet. Fix anything that's off, then confirm.
      </p>
      <ul className="mt-2.5 divide-y divide-line-1 rounded-md bg-fill-1">
        {proposals.map((p) => {
          const value = edits[p.field] ?? displayValue(p.value);
          const editing = fixing === p.field;
          return (
            <li
              key={p.field}
              className="flex min-h-10 items-center gap-3 py-1.5 pr-1.5 pl-2.5"
              data-testid={`prefill-${p.field}`}
            >
              <div className="w-28 shrink-0 text-sm text-fg-3">
                {p.label || p.field}
              </div>
              <div className="min-w-0 flex-1">
                {editing ? (
                  <Input
                    autoFocus
                    value={value}
                    aria-label={p.label || p.field}
                    onChange={(e) =>
                      setEdits((cur) => ({ ...cur, [p.field]: e.target.value }))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setFixing(null);
                    }}
                  />
                ) : (
                  <div className="truncate text-fg">{value}</div>
                )}
                <div className="mt-0.5 text-xs text-fg-4">
                  {SOURCE_TEXT[p.source] ?? `From ${p.source}`}
                </div>
              </div>
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setFixing(editing ? null : p.field)}
              >
                {editing ? <Check /> : <Pencil />}
                {editing ? "Done" : "Fix"}
              </Button>
            </li>
          );
        })}
      </ul>
      {say ? (
        <div className="mt-2 flex items-center justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-fg-3"
            disabled={busy}
            onClick={() => {
              setSent("skipped");
              say("Skip these for now; don't save them.");
            }}
            data-testid="prefill-skip"
          >
            Skip
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={busy}
            onClick={() => {
              setSent("saved");
              setFixing(null);
              say(confirmationMessage(proposals, edits));
            }}
            data-testid="prefill-confirm"
          >
            Looks Right
          </Button>
        </div>
      ) : null}
    </div>
  );
};
